'use client'

// Import libraries
import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react'
import {FlattedCategory} from '@/types/category'
import {FilePreview} from "@/components/Form/types";
import {getCompanyId} from '@/utils/getCompanyId'
import {v4 as uuid} from 'uuid'
import { startNotif, updateNotif, closeNotif } from '@/components/DynamicForm/util/notifier';

// Import Services
import getCategories from '@/services/CategoryService/getCategories'
import upload from "@/services/upload";
import addImage from "@/services/CategoryService/addImages";
import updateCategoryImagesByName from "@/services/CategoryService/updateCategoryImagesByName";

// Import UI Components
import Modal from '@/components/Modal/Modal'
import Alert from "@/components/UiElements/Alert";
import ImageComponent from "@/components/UiElements/ImageComponent";
import Heading from '@/components/Heading/Heading'
import Splash from "@/components/splash/Splash";
import SearchBar, {SearchBarFilters, SearchBarOptions} from "@/components/UiElements/SearchBar";
import FileDropOverlay from "@/components/UiElements/FileDropOverlay";
import Button from "@/components/UiElements/Button";


// Import Category Components
import MediaList from "@/components/Categories/components/MediaList";
import CategoryStatus from "@/components/Categories/components/CategoryStatus";
import CreateCategoryModal from "@/components/Categories/components/CreateCategoryModal";

// Import Utils
import convertToSvg from "@/components/Categories/utils/cleanSvg/convertToSvg";
import convertToPng from "@/components/Categories/utils/cleanSvg/convertToPng";
import sortByPriorityThenId from '@/utils/sortByPriorityThenId';
import {toast} from "react-toastify";
import notifyError from "@/utils/notifyError";





type CategoriesPageProps = {
    hideHeader?: boolean;
}

type MarqueeBox = {
    left: number;
    top: number;
    width: number;
    height: number;
}

type MarqueeDragState = {
    pointerId: number;
    startX: number;
    startY: number;
    baseSelection: Set<string>;
    isDragging: boolean;
    lastSelectedId: string | null;
}

const marqueeDragThreshold = 4;

const createMarqueeBox = (startX: number, startY: number, endX: number, endY: number): MarqueeBox => ({
    left: Math.min(startX, endX),
    top: Math.min(startY, endY),
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
});

const boxesIntersect = (selectionBox: MarqueeBox, itemBox: DOMRect) => (
    selectionBox.left < itemBox.right &&
    selectionBox.left + selectionBox.width > itemBox.left &&
    selectionBox.top < itemBox.bottom &&
    selectionBox.top + selectionBox.height > itemBox.top
);

const areSetsEqual = (first: Set<string>, second: Set<string>) => (
    first.size === second.size && [...first].every((value) => second.has(value))
);

const sortGraphicElements = (items: FlattedCategory[]) => {
    const sortedGraphics = items
        .filter((item) => item.type === 'image')
        .sort(sortByPriorityThenId);
    let graphicIndex = 0;

    return items.map((item) => (
        item.type === 'image' ? sortedGraphics[graphicIndex++] : item
    ));
}

const CategoriesPage: React.FC<CategoriesPageProps> = ({hideHeader = false}) => {

    const [categories, setCategories] = useState<FlattedCategory[]>([])
    const [mediaList, setMediaList] = useState<FlattedCategory[]>([])
    const [parentId, setParentId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>('');
    const [editCategory, setEditCategory] = useState<FlattedCategory | null | undefined>(null)
    const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false)
    const [uploadQueue, setUploadQueue] = useState<FlattedCategory[]>([]); // Queue to manage ongoing uploads
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [marqueeBox, setMarqueeBox] = useState<MarqueeBox | null>(null);

    const graphicInputRef = useRef<HTMLInputElement | null>(null);
    const categoryGridRef = useRef<HTMLDivElement | null>(null);
    const categoriesRef = useRef(categories);
    const lastSelectedGraphicIdRef = useRef<string | null>(null);
    const marqueeDragStateRef = useRef<MarqueeDragState | null>(null);
    const suppressNextPageClickRef = useRef(false);




    // -----------------------------------------------
    // Initialize required variables
    // -----------------------------------------------
    const companyId = getCompanyId();

    const parentBreadCrumb = useMemo<FlattedCategory[]>(() => {
        const buildBreadCrumb = (parentId: string | null, categories: FlattedCategory[]): FlattedCategory[] => {
            const breadcrumb: FlattedCategory[] = [];
            let currentCategory = categories.find(category => category.id === parentId);
            while (currentCategory !== undefined) {
                breadcrumb.unshift(currentCategory);
                currentCategory = currentCategory?.parent
                    ? categories.find(category => category.id === currentCategory?.parent)
                    : undefined;
            }
            return breadcrumb;
        };
        return buildBreadCrumb(parentId, categories);
    }, [categories, parentId]);

    const currentCategory = useMemo<FlattedCategory | undefined>(() => {
        return categoriesRef.current.find(category => category.id === parentId);
    }, [categories, parentId, mediaList]);

    const categorySearchFilters = useMemo<SearchBarFilters[]>(() => {
        return [
            {
                label: 'Show Inactive Items',
                onClick: (value: boolean) => {
                    console.log('Show Inactive Items:', value, categories);
                    setShowInactive(value);
                },
                count: categories.filter((media) => !media.isActive && media.type === "category" && media.parent === parentId).length,
            },
        ];
    }, [categories, mediaList]);

    const visibleGraphicIds = useMemo(() => {
        return mediaList.filter((m) => m.type === 'image').map((m) => m.id)
    }, [mediaList])

    const handleToggleImageSelection = useCallback((graphic: FlattedCategory, shiftKey = false) => {
        setSelectedImages((prev) => {
            const next = new Set(prev);
            const shouldSelect = !next.has(graphic.id);
            const lastSelectedId = lastSelectedGraphicIdRef.current;

            if (shiftKey && lastSelectedId) {
                const currentIndex = visibleGraphicIds.indexOf(graphic.id);
                const lastIndex = visibleGraphicIds.indexOf(lastSelectedId);

                if (currentIndex !== -1 && lastIndex !== -1) {
                    const rangeStart = Math.min(currentIndex, lastIndex);
                    const rangeEnd = Math.max(currentIndex, lastIndex);

                    visibleGraphicIds.slice(rangeStart, rangeEnd + 1).forEach((id) => {
                        if (shouldSelect) {
                            next.add(id);
                        } else {
                            next.delete(id);
                        }
                    });
                } else if (shouldSelect) {
                    next.add(graphic.id);
                } else {
                    next.delete(graphic.id);
                }
            } else if (shouldSelect) {
                next.add(graphic.id);
            } else {
                next.delete(graphic.id);
            }

            return next;
        });
        lastSelectedGraphicIdRef.current = graphic.id;
    }, [visibleGraphicIds])

    const handleDeselectAll = useCallback(() => {
        setSelectedImages(new Set());
        lastSelectedGraphicIdRef.current = null;
    }, [])

    const handleMarqueePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || event.target !== event.currentTarget) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);

        const preserveCurrentSelection = event.shiftKey || event.ctrlKey || event.metaKey;
        marqueeDragStateRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            baseSelection: preserveCurrentSelection ? new Set(selectedImages) : new Set<string>(),
            isDragging: false,
            lastSelectedId: null,
        };
    }, [selectedImages]);

    const handleMarqueePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = marqueeDragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        const horizontalDistance = event.clientX - dragState.startX;
        const verticalDistance = event.clientY - dragState.startY;

        if (!dragState.isDragging && Math.hypot(horizontalDistance, verticalDistance) < marqueeDragThreshold) {
            return;
        }

        event.preventDefault();
        dragState.isDragging = true;

        const nextMarqueeBox = createMarqueeBox(
            dragState.startX,
            dragState.startY,
            event.clientX,
            event.clientY,
        );
        const nextSelection = new Set(dragState.baseSelection);
        let lastSelectedId: string | null = null;

        categoryGridRef.current
            ?.querySelectorAll<HTMLElement>('[data-category-graphic-id]')
            .forEach((graphicElement) => {
                const graphicId = graphicElement.dataset.categoryGraphicId;
                if (!graphicId || !boxesIntersect(nextMarqueeBox, graphicElement.getBoundingClientRect())) return;

                nextSelection.add(graphicId);
                lastSelectedId = graphicId;
            });

        dragState.lastSelectedId = lastSelectedId;
        setMarqueeBox(nextMarqueeBox);
        setSelectedImages((currentSelection) => (
            areSetsEqual(currentSelection, nextSelection) ? currentSelection : nextSelection
        ));
    }, []);

    const finishMarqueeSelection = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const dragState = marqueeDragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        if (dragState.isDragging) {
            suppressNextPageClickRef.current = true;
            lastSelectedGraphicIdRef.current = dragState.lastSelectedId;
            window.setTimeout(() => {
                suppressNextPageClickRef.current = false;
            }, 0);
        }

        marqueeDragStateRef.current = null;
        setMarqueeBox(null);
    }, []);

    const categorySearchOptions = useMemo<SearchBarOptions[]>(() => {
        if (selectedImages.size > 0) {
            return [
                {
                    label: (
                        <span className="flex flex-row gap-md" data-category-selection-action>
                            <span className="font-icon-solid"></span>
                            Cancel
                        </span>
                    ),
                    onClick: handleDeselectAll,
                },
                {
                    label: (
                        <span className="flex flex-row gap-md" data-category-selection-action>
                            <span className="font-icon-solid"></span>
                            Delete Selected ({selectedImages.size})
                        </span>
                    ),
                    onClick: () => handleDeleteSelectedGraphics(),
                    type: 'danger' as const,
                },
            ];
        }

        const options: SearchBarOptions[] = [];
        options.push({
            label: (
                <span className="flex flex-row gap-md">
                    <span className="font-icon-solid text-primary"></span>
                    New {parentId ? "Subcategory" : "Category"}
                </span>
            ),
            onClick: () => setIsCreateCategoryModalOpen(true),
        })

        return options;
    }, [parentId, selectedImages, handleDeselectAll]);




    // -----------------------------------------------
    // Navigate to categories
    // -----------------------------------------------
    const handleSelectCategory = (category: FlattedCategory) => {
        setParentId(category.id);
    }

    const handleBackCategory = () => {
        const id:string|null = parentBreadCrumb[parentBreadCrumb.length - 2]?.id || null;
        setParentId(id);
    }

    const handleOnSearch = (text: string) => {
        setSearchString(text);
    }

    const handlePageClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (suppressNextPageClickRef.current) {
            suppressNextPageClickRef.current = false;
            return;
        }

        if (selectedImages.size === 0) return;

        const target = event.target;
        if (target instanceof Element) {
            const selectionAction =
                target.closest('[data-category-selection-action]') ||
                target.closest('button')?.querySelector('[data-category-selection-action]');

            if (target.closest('[data-category-graphic-item]') || selectionAction) return;
        }

        setSelectedImages(new Set());
        lastSelectedGraphicIdRef.current = null;
    }

    const handleCloseCategoryModal = () => {
        setEditCategory(null)
        setIsCreateCategoryModalOpen(false)
    }




    // -----------------------------------------------
    // Category service calls
    // -----------------------------------------------
    const handleLoadCategories = async (): Promise<void> => {
        setIsLoading(true);
        const list:FlattedCategory[] = await getCategories();
        setCategories(list);
        setIsLoading(false);
    };
    const handleSaveCategory = async () => {
        await handleLoadCategories();
        handleCloseCategoryModal();
    }
    const updateCategoriesHierarchy = (categories: FlattedCategory[]): FlattedCategory[] => {
        return categories.map((item) => {
            const updatedItem: FlattedCategory = { ...item };
            if (item.type === "category") {
                updatedItem.childCount = categories.filter(
                    (c) => c.parent === item.id && c.type === "image" && (c.uploading === "done" || c.uploading === null)
                ).length || 0;

                updatedItem.childCategory = categories.filter(
                    (c) => c.parent === item.id && c.type === "category"
                ).length || null;
            }
            return updatedItem;
        });
    };





    // -----------------------------------------------
    // Graphic service calls
    // -----------------------------------------------
    const changeType = (id: string, type: 'category' | 'image' | 'upload' | 'delete' | 'deleted') => {
        setCategories((prevItems: FlattedCategory[]) => {
            return prevItems.map(item=> {
                if (item.id === id) {
                    return {...item, type: type}
                }
                return item
            })
        });
    }
    const handleDeleteGraphic = async (image: FlattedCategory) => {

        Alert.showAlert({
            title: "Delete Graphic",
            message:
                <div className={"flex flex-col items-center justify-center "}>
                    <div className={"border border-gray-300 p-sm rounded-md my-md"}>
                        <ImageComponent src={image.img} className={"!w-[100px] !h-[100px]"} />
                    </div>
                    Are you sure you want to delete this image?
                </div>,
            callBack: async (ok) => {
                console.log('image:', image);
                if (!ok) return;
                if (image.parent) {

                    const filteredImages = categoriesRef.current.filter((element) => element.id !== image.id && element.type === "image" && element.parent === image.parent) || [];
                    const filteredIds = filteredImages.map((img:FlattedCategory) => img.id);

                    changeType(image.id, "delete");

                    //const notifId = startNotif(`Deleting graphic from "${image.name}"`);


                    await new Promise<void>((resolve) => {
                        setTimeout(() => {
                            resolve(); // This ensures the await statement works properly
                        }, 100);
                    });

                    const categoryUpdated = updateCategoryImagesByName(filteredIds, image.parent);
                    if (!categoryUpdated) {
                        changeType(image.id, "image");
                        notifyError(`Failed to delete image from "${image.name}`);
                        return;
                    } else {
                        changeType(image.id, "deleted");
                    }
                }
            },
            isCancelable: true,
            danger: true,
            button: "Delete!",

        });
    }
    const handleDeleteSelectedGraphics = () => {
        if (selectedImages.size === 0) return;

        Alert.showAlert({
            title: 'Delete Selected Graphics',
            message: (
                <div className="flex flex-col items-center justify-center">
                    Are you sure you want to delete {selectedImages.size} selected image{selectedImages.size > 1 ? 's' : ''}?
                </div>
            ),
            callBack: async (ok) => {
                if (!ok) return;
                if (!parentId) return;

                const selectedIds = Array.from(selectedImages);
                const selectedGraphics = categoriesRef.current.filter((item) => selectedImages.has(item.id));
                const selectedCount = selectedIds.length;
                const notificationId = startNotif(
                    `Deleting ${selectedCount} selected graphic${selectedCount === 1 ? '' : 's'}...`,
                );

                // Mark all selected as deleting
                selectedIds.forEach((id) => changeType(id, 'delete'));

                await new Promise<void>((resolve) => setTimeout(resolve, 100));

                const remainingImages = categoriesRef.current.filter(
                    (el) => !selectedImages.has(el.id) && el.type === 'image' && el.parent === parentId
                );
                const remainingIds = remainingImages.map((img) => img.id);

                try {
                    const categoryUpdated = await updateCategoryImagesByName(remainingIds, parentId);
                    if (!categoryUpdated) throw new Error('Category update failed');

                    selectedIds.forEach((id) => changeType(id, 'deleted'));
                    setSelectedImages(new Set());
                    lastSelectedGraphicIdRef.current = null;

                    let undoInProgress = false;
                    const handleUndo = async () => {
                        if (undoInProgress) return;
                        undoInProgress = true;

                        toast.dismiss(notificationId);
                        const undoNotificationId = startNotif(
                            `Restoring ${selectedCount} graphic${selectedCount === 1 ? '' : 's'}...`,
                        );

                        const activeImageIds = categoriesRef.current
                            .filter((item) => item.parent === parentId && item.type === 'image')
                            .map((item) => item.id);
                        const restoredImageIds = [...new Set([...activeImageIds, ...selectedIds])];

                        try {
                            const categoryRestored = await updateCategoryImagesByName(restoredImageIds, parentId);
                            if (!categoryRestored) throw new Error('Category restore failed');

                            setCategories((currentCategories) => {
                                const restoredIds = new Set(selectedIds);
                                const nextCategories = currentCategories.map((item) => (
                                    restoredIds.has(item.id) ? {...item, type: 'image' as const} : item
                                ));
                                const existingIds = new Set(nextCategories.map((item) => item.id));

                                selectedGraphics.forEach((graphic) => {
                                    if (!existingIds.has(graphic.id)) {
                                        nextCategories.push({...graphic, type: 'image'});
                                    }
                                });

                                return updateCategoriesHierarchy(nextCategories);
                            });
                            closeNotif(
                                undoNotificationId,
                                `${selectedCount} graphic${selectedCount === 1 ? '' : 's'} restored.`,
                            );
                        } catch {
                            closeNotif(
                                undoNotificationId,
                                'Failed to restore the deleted graphics.',
                                {type: 'error'},
                            );
                        }
                    };

                    closeNotif(
                        notificationId,
                        <div className="relative z-[1] flex items-center justify-between gap-md">
                            <span>{`${selectedCount} graphic${selectedCount === 1 ? '' : 's'} deleted.`}</span>
                            <button
                                type="button"
                                className="relative z-[2] rounded-md px-md py-xs font-normal text-primary-700 transition-all duration-200 hover:bg-primary-500 hover:text-white hover:text-shadow-sm"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void handleUndo();
                                }}
                            >
                                Undo
                            </button>
                        </div>,
                        {
                            autoCloseTime: 10000,
                            closeOnClick: false,
                            pauseOnFocusLoss: false,
                            pauseOnHover: false,
                        },
                    );
                } catch {
                    selectedIds.forEach((id) => changeType(id, 'image'));
                    closeNotif(notificationId, 'Failed to delete the selected graphics.', {type: 'error'});
                }
            },
            isCancelable: true,
            danger: true,
            button: `Delete ${selectedImages.size} Image${selectedImages.size > 1 ? 's' : ''}!`,
        });
    }

    const handleRemoveGraphic = (image: FlattedCategory) => {
        setCategories((currentCategories) => (
            updateCategoriesHierarchy(currentCategories.filter((element) => element.id !== image.id))
        ));
    }
    const handleOpenFile = async () => {
        graphicInputRef.current?.click();
    }
    const handleAddElement = (files: File[]) => {
        if (!currentCategory) return
        if (graphicInputRef.current) graphicInputRef.current.value = '';
        const newCategories: FlattedCategory[] = files.map((file) => ({
            id: 'new-category-' + uuid(),
            name: currentCategory.name,
            description: '',
            parent: parentId,
            type: 'upload',
            isActive: true,
            img: URL.createObjectURL(file),
            file: file,
            showOnHome: false,
            uploading: 'idle',
        }));

        setCategories((prevCategories) => [...prevCategories, ...newCategories]);
        setUploadQueue((prevQueue) => [...prevQueue, ...newCategories]);
    };


    const handleUploadGraphicsQueue = async (list: FlattedCategory[]) => {

        const totalCount = list.length;
        const toastId = startNotif(`Uploading 0/${totalCount} graphics to ${list[0].name}`);

        const queue: FlattedCategory[] = list.map((graphic) => ({ ...graphic, uploading: 'uploading' }));
        setUploadQueue(queue);

        for (let i = 0; i < list.length; i++) {
            const graphic = list[i];
            try {
                updateNotif(toastId, `Uploading ${i + 1}/${totalCount} graphics to "${list[0].name}"`, i / totalCount);
                await uploadGraphic(graphic);
                setUploadQueue((prevQueue) => prevQueue.map((g) => {
                    if (g.id === graphic.id) {
                        return { ...g, uploading: 'done' };
                    }
                    return g;
                }));
                updateNotif(toastId, `Uploading ${i + 1}/${totalCount} graphics to "${list[0].name}"`, (i + 1) / totalCount);
            } catch (error) {
                setUploadQueue((prevQueue) => prevQueue.map((g) => {
                    if (g.id === graphic.id) {
                        return { ...g, uploading: 'error' };
                    }
                    return g;
                }));
                updateNotif(toastId, `Failed to upload ${graphic.name}`, (i + 1) / totalCount);
            }
        }

        closeNotif(toastId, `Upload ${totalCount} graphics to "${list[0].name}" completed`, { type: 'success' });

    };


    const uploadGraphic = useCallback(async (graphicItem: FlattedCategory) => {
        return new Promise<void>(async (resolve) => {
            if (graphicItem.file && graphicItem.parent) {
                try {


                    const image = await convertToPng(graphicItem.img);
                    if (!image) return

                    const imageFile = new File([image], graphicItem.file.name, { type: 'image/png' });
                    const vector = await convertToSvg(imageFile);

                    const svgBlob = new Blob([vector], { type: 'image/svg+xml' });

                    const reader = new FileReader();
                    const base64Data = await new Promise<string>((resolve, reject) => {
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(svgBlob);
                    });
                    if (!base64Data) throw new Error('Failed to convert SVG to base64');


                    const uploadedUrl = await upload({
                        file: base64Data,
                        fileName: `graphic_${graphicItem.id}_${Date.now()}.svg`,
                        folderName: "categories",
                    })
                    if (!uploadedUrl) throw new Error('Failed to upload SVG');


                    const addedImageId = await addImage(uploadedUrl)
                    if (!addedImageId) throw new Error('Failed to add image to category');
                    const categoryImages = categoriesRef.current.filter((element) => element.parent === graphicItem.parent && element.type=== "image") || [];
                    const imageIds = categoryImages?.map((img:FlattedCategory) => img.id);

                    imageIds.push(addedImageId);

                    const categoryUpdated = updateCategoryImagesByName(imageIds, graphicItem.parent);
                    if (!categoryUpdated) throw new Error('Failed to update category images');

                    setCategories((prevCategories) => {
                        const modifiedCategories: FlattedCategory[] = prevCategories.map((element) => {
                            if (element.id === graphicItem.id) {
                                URL.revokeObjectURL(element.img);
                                return {
                                    ...element,
                                    uploading: 'done',
                                    type: 'image',
                                    img: uploadedUrl || element.img,
                                    id: addedImageId,
                                };
                            }
                            return element;
                        });

                        return updateCategoriesHierarchy(modifiedCategories);
                    });
                    resolve();

                } catch (error) {
                    setCategories((prevCategories) =>
                        prevCategories.map((element) => {
                            if (element.id === graphicItem.id) {
                                URL.revokeObjectURL(element.img);
                                return { ...element, uploading: 'error' };
                            }
                            return element;
                        })
                    );
                    console.error('Error during PNG conversion:', error);
                    resolve();
                }
            } else {
                resolve();
            }
        });
    },[categories]);





    useEffect(() => {
        const pendingList:FlattedCategory[] = uploadQueue.filter((graphic) => graphic.uploading === 'idle');
        if (pendingList.length > 0) {
            handleUploadGraphicsQueue(pendingList);
        }
    }, [uploadQueue]);




    // -----------------------------------------------
    // useEffect functions to handle media changes
    // -----------------------------------------------
    useEffect(() => {
        let media:FlattedCategory[] = categories?.filter((m) =>m.parent === parentId)
        if (searchString) {
            media = media.filter((m) =>
                m.name.toLowerCase().includes(searchString.toLowerCase()) || m.description.toLowerCase().includes(searchString.toLowerCase())
            );
        }
        if (!showInactive) {
            media = media.filter((m) => m.isActive)
        }
        setMediaList(sortGraphicElements(media));
    }, [parentId, showInactive, searchString, categories]);




    // -----------------------------------------------
    // useEffect functions to handle load categories on companyId change
    // -----------------------------------------------
    useEffect(() => {
        handleLoadCategories()
    }, [companyId])

    useEffect(() => {
        categoriesRef.current = categories;
    }, [categories]);

    useEffect(() => {
        setSelectedImages(new Set());
        lastSelectedGraphicIdRef.current = null;
    }, [parentId]);




    const categoryInfo = parentBreadCrumb.length > 0 && currentCategory ? (
        <div className="mb-[20px] flex min-h-[164px] flex-row gap-xxl items-start">
            <div className="relative block w-[150px] h-[150px] mx-[6px] mb-[6px] shrink-0 before:block before:content-[''] before:w-full before:h-full before:absolute before:top-0 before:left-0 before:bg-white before:border before:border-gray-300 before:-rotate-6 before:rounded-md before:z-[1] after:block after:content-[''] after:w-full after:h-full after:absolute after:top-0 after:left-0 after:bg-white after:border after:border-gray-300 after:rotate-3 after:rounded-md after:z-[1]">
                <div className="relative z-[2] w-full h-full min-w-0 min-h-0 bg-white border border-gray-300 rounded-md p-[10px] flex items-center justify-center overflow-hidden">
                    <ImageComponent
                        src={currentCategory.img}
                        className="!w-full !h-full !max-w-full !max-h-full"
                        style={{objectFit: 'contain'}}
                    />
                </div>
            </div>
            <div className="flex flex-1 min-w-0 flex-col justify-start gap-xs">
                <div className="flex flex-row gap-md items-start">
                    <div className="flex flex-1 min-w-0 flex-row flex-wrap gap-md items-center">
                        <div
                            className="text-sm text-gray-400 hover:text-primary-700 transition-all cursor-pointer"
                            onClick={()=>setParentId(null)}
                        >
                            Home
                        </div>
                        {parentBreadCrumb.map((item, index)=> (
                            <div
                                key={item.id}
                                className={`hover:text-primary-700 cursor-pointer transition-all text-sm flex items-center justify-center flex-row gap-md ${index === parentBreadCrumb.length -1  ? "text-gray-900" : "text-gray-400"}`}
                                onClick={()=>setParentId(item.id)}
                            >
                                <span className="font-icon-light text-gray-300"></span>
                                {item.name}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex flex-row flex-wrap items-stretch gap-xs">
                    <CategoryStatus category={currentCategory} />
                    {selectedImages.size === 0 && (
                        <Button
                            type="alt"
                            className="self-stretch !w-auto !min-w-[125px] !h-auto shrink-0"
                            onClick={() => {
                                setEditCategory(currentCategory)
                                setIsCreateCategoryModalOpen(true)
                            }}
                        >
                            <span className="flex items-center gap-xs">
                                <span className="font-icon-solid"></span>
                                <span>Edit Category</span>
                            </span>
                        </Button>
                    )}
                </div>
                <div className="text-neutral-600 text-sm font-normal flex flex-row">
                    <span className="text-gray-400 w-[100px] shrink-0">Description: </span>
                    <div className="flex flex-row gap-sm">
                        {currentCategory.description}
                    </div>
                </div>
            </div>
        </div>
    ) : null;



    return (
        <div onClick={handlePageClick}>

            <Modal isOpen={isCreateCategoryModalOpen} onClose={handleCloseCategoryModal} title={`${editCategory? "Edit Category" : "Add Category"}`} closeOnOutsideClick={false}>
                {isCreateCategoryModalOpen &&
                    <CreateCategoryModal
                        onClose={handleCloseCategoryModal}
                        parentId={parentId}
                        categoryList={categories}
                        currentCategory={editCategory}
                        updateCategoryCallback={handleSaveCategory}
                    />
                }
            </Modal>


            {!hideHeader && (
                <Heading
                    title={"Graphic Categories"}
                    description={'Create and manage categories, subcategories, and organize graphic elements within them.'}
                />
            )}



            <div className={"pr-xxl block min-h-fit pb-xxxl"}>
                {categoryInfo}

                <div className="sticky top-[48px] bg-white z-[10]">
                    <SearchBar
                        placeholder={"Search by category name"}
                        filters={categorySearchFilters}
                        options={categorySearchOptions}
                        onChange={handleOnSearch}
                    />
                </div>

                <div
                    ref={categoryGridRef}
                    className="relative flex min-h-[200px] cursor-crosshair flex-row flex-wrap gap-md gap-y-xl pt-xxl"
                    onPointerDown={handleMarqueePointerDown}
                    onPointerMove={handleMarqueePointerMove}
                    onPointerUp={finishMarqueeSelection}
                    onPointerCancel={finishMarqueeSelection}
                >
                    {marqueeBox && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none fixed z-[9] border-2 border-dashed border-primary-500 bg-primary-500/10"
                            style={marqueeBox}
                        />
                    )}
                    {isLoading && <Splash /> }
                    {parentId && (
                        <div className={"w-[164px] h-[164px] p-[6px] select-none"}>
                            <div
                                onClick={handleBackCategory}
                                className={"group flex cursor-pointer justify-center items-center  border rounded-md h-[150px] w-[164px] text-primary-700 border-primary-400 bg-primary-25  hover:border-primary-500 focus:border-primary-500 active:shadow-focus active:bg-white"}
                            >
                                <div className="w-[37px] h-[37px] bg-primary-25 rounded-full flex-col justify-center items-center inline-flex font-icon-regular group-hover:bg-primary-500 group-active:bg-primary-200 transition-all">
                                    <div className="text-center text-primary-700 group-hover:text-white group-active:text-primary-700"></div>
                                </div>
                            </div>

                        </div>
                    )}

                    {parentId && (
                        <div className="w-[164px] h-[164px] p-[6px] select-none">
                            <button
                                type="button"
                                aria-label="Add graphic"
                                data-category-selection-action
                                onClick={handleOpenFile}
                                className="group flex cursor-pointer justify-center items-center border rounded-md h-[150px] w-[164px] text-primary-700 border-primary-400 bg-primary-25 hover:border-primary-500 focus:border-primary-500 active:shadow-focus active:bg-white"
                            >
                                <span className="h-[37px] px-md bg-primary-25 rounded-full flex justify-center items-center gap-sm text-title-md text-primary-700 group-hover:bg-primary-500 group-hover:text-white group-active:bg-primary-200 group-active:text-primary-700 transition-all">
                                    <span className="font-icon-solid"></span>
                                    Add Image
                                </span>
                            </button>
                        </div>
                    )}

                    {mediaList.length === 0 && (
                        <div className={"w-[164px] h-[164px] p-[6px] select-none"}>
                            <div
                                className={"group flex justify-center items-center rounded-md h-[150px] w-[164px] text-gray-300  bg-gray-50 "}
                            >
                                {searchString ? "No Result" : "Empty"}
                            </div>

                        </div>
                    )}
                    <MediaList
                        mediaList={mediaList}
                        handleSelectCategory={handleSelectCategory}
                        handleDeleteGraphic={handleDeleteGraphic}
                        handleRemoveGraphic={handleRemoveGraphic}
                        selectedImages={selectedImages}
                        onToggleSelect={handleToggleImageSelection}
                    />

                </div>

                <div className={"h-[100px]"}>
                    {(parentId) && (
                        <>
                            <div className={"text-gray-400 text-text-md pt-xxxl select-none "}>
                                <span className={"font-icon-light pr-md pl-[6px]"}></span>
                                You can add a new graphic element by dragging it onto the page or by clicking the <u
                                    className={"cursor-pointer hover:text-primary-700 transition-all"}
                                    onClick={() => handleOpenFile()}
                                >
                                Add Graphic
                                </u> button.
                            </div>
                            {!isCreateCategoryModalOpen && (
                                <FileDropOverlay
                                    onDrop={(files: FilePreview[]) => {
                                        const fileList: File[] = files
                                            .map(f => f.data)
                                            .filter((f): f is File => f !== null);
                                        handleAddElement(fileList)
                                    }}
                                    accept={'.jpg, .jpeg, .png, .svg, .webp, image/png, image/jpeg, image/jpg, image/svg+xml, text/svg+xml, image/webp'}
                                />
                            )}
                            <input
                                ref={graphicInputRef}
                                type="file"
                                accept={'.jpg, .jpeg, .png, .svg, .webp, image/png, image/jpeg, image/jpg, image/svg+xml, text/svg+xml, image/webp'}
                                className="hidden"
                                style={{ display: 'none' }}
                                multiple
                                onChange={(event)=>  {
                                    if (event.target.files) {
                                        const files = Array.from(event.target.files)
                                        handleAddElement(files)
                                    }
                                }}

                            />

                        </>
                    )}
                </div>


            </div>

        </div>
    );
}

export default CategoriesPage
